import { useEffect, useState } from 'react';
import { Alert, Button, Skeleton } from '@tb/ui';
import type { SaveDraftRequest } from '@tb/shared';
import { api } from '../../api/endpoints';
import { useT } from '../../i18n/LocaleProvider';

export interface PreviewPaneProps {
  siteId: string;
  draft: SaveDraftRequest;
}

export function PreviewPane({ siteId, draft }: PreviewPaneProps) {
  const [html, setHtml] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const t = useT();

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    const timer = window.setTimeout(() => {
      void (async () => {
        try {
          const rendered = await api.sites.preview(siteId, draft);
          if (!cancelled) {
            setHtml(rendered);
            setError(null);
          }
        } catch (requestError) {
          if (!cancelled) setError((requestError as Error).message);
        }
      })();
    }, 500);

    return () => {
      cancelled = true;
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [siteId, draft, nonce]);

  if (error) {
    return (
      <div className="tb-stack">
        <Alert tone="danger" title={t('editor.preview.failed')}>
          {error}
        </Alert>
        <Button variant="secondary" icon="refresh" onClick={() => setNonce((value) => value + 1)}>
          {t('editor.preview.retry')}
        </Button>
      </div>
    );
  }

  if (html === null) return <Skeleton height="60vh" />;

  return (
    <div className="tb-stack">
      <iframe
        className="preview-frame"
        title="Site preview"
        sandbox="allow-scripts allow-popups"
        srcDoc={html}
      />
      <span className="tb-hint">{t('editor.preview.note')}</span>
    </div>
  );
}
